'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Search, Plus, Filter, Eye, Pencil, Trash2, Copy, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, FileText, TrendingUp, CheckCircle2, XCircle, Clock, Send } from 'lucide-react';
import { Quotation, QuotationStatus } from '@/lib/types';
import { getQuotations, deleteQuotation, saveQuotation } from '@/lib/store';
import { formatINR } from '@/lib/calculations';
import StatusBadge from '@/components/ui/StatusBadge';

const STATUS_OPTIONS: QuotationStatus[] = ['Draft', 'Sent', 'Approved', 'Rejected'];

type SortField = 'quoteNumber' | 'date' | 'clientName' | 'grandTotal' | 'status';
type SortDir = 'asc' | 'desc';

const PAGE_SIZES = [10, 20, 50];

export default function QuotationsManagementContent() {
  const router = useRouter();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<QuotationStatus | 'All'>('All');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [statusMenuId, setStatusMenuId] = useState<string | null>(null);

  const load = useCallback(() => setQuotations(getQuotations()), []);
  useEffect(() => { load(); }, [load]);

  // Stats
  const counts = quotations.reduce((acc, q) => {
    acc[q.status] = (acc[q.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const totalValue = quotations.reduce((s, q) => s + q.grandTotal, 0);

  // Filter + sort
  const filtered = quotations
    .filter(q => {
      const matchSearch =
        q.quoteNumber.toLowerCase().includes(search.toLowerCase()) ||
        q.clientName.toLowerCase().includes(search.toLowerCase()) ||
        q.clientCompany.toLowerCase().includes(search.toLowerCase()) ||
        q.subject.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'All' || q.status === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'quoteNumber') cmp = a.quoteNumber.localeCompare(b.quoteNumber);
      else if (sortField === 'date') {
        const pa = a.date.split('/').reverse().join('');
        const pb = b.date.split('/').reverse().join('');
        cmp = pa.localeCompare(pb);
      }
      else if (sortField === 'clientName') cmp = a.clientName.localeCompare(b.clientName);
      else if (sortField === 'grandTotal') cmp = a.grandTotal - b.grandTotal;
      else if (sortField === 'status') cmp = a.status.localeCompare(b.status);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  function handleDelete(id: string) {
    deleteQuotation(id);
    load();
    setDeleteId(null);
    toast.success('Quotation deleted');
  }

  function handleDuplicate(q: Quotation) {
    const all = getQuotations();
    const newQ: Quotation = {
      ...q,
      id: `qt-${Date.now()}`,
      quoteNumber: `PT/${new Date().getFullYear()}/${String(all.length + 1).padStart(4, '0')}`,
      status: 'Draft',
      date: new Date().toLocaleDateString('en-GB'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    saveQuotation(newQ);
    load();
    toast.success(`Duplicated as ${newQ.quoteNumber}`);
  }

  function handleStatusChange(q: Quotation, status: QuotationStatus) {
    saveQuotation({ ...q, status });
    load();
    setStatusMenuId(null);
    toast.success(`Status updated to ${status}`);
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <ChevronUp size={12} className="text-slate-300" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-[#E8620A]" /> : <ChevronDown size={12} className="text-[#E8620A]" />;
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Quotations</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage and track all PEAKTIME quotations</p>
        </div>
        <Link
          href="/quotation-editor"
          className="flex items-center gap-2 bg-[#E8620A] hover:bg-[#d05508] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 shadow-sm"
        >
          <Plus size={16} />
          New Quotation
        </Link>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Total Quotes', value: quotations.length, icon: FileText, color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200' },
          { label: 'Draft', value: counts['Draft'] || 0, icon: Clock, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
          { label: 'Sent', value: counts['Sent'] || 0, icon: Send, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
          { label: 'Approved', value: counts['Approved'] || 0, icon: CheckCircle2, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
          { label: 'Rejected', value: counts['Rejected'] || 0, icon: XCircle, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
        ].map(s => (
          <div key={`stat-${s.label}`} className={`${s.bg} border ${s.border} rounded-xl px-4 py-3 flex items-center gap-3`}>
            <s.icon size={20} className={s.color} />
            <div>
              <p className="text-xs font-500 text-slate-500">{s.label}</p>
              <p className={`text-xl font-bold tabular-nums ${s.color}`}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Total pipeline value */}
      <div className="bg-gradient-to-r from-[#1B2A4A] to-[#243556] rounded-xl px-5 py-3 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TrendingUp size={18} className="text-[#C9922A]" />
          <span className="text-white text-sm font-medium">Total Pipeline Value</span>
        </div>
        <span className="text-[#C9922A] font-bold text-lg tabular-nums">{formatINR(totalValue)}</span>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by quote #, client, or subject..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A] bg-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={15} className="text-slate-400" />
          {(['All', ...STATUS_OPTIONS] as const).map(s => (
            <button
              key={`filter-${s}`}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-150 border ${
                statusFilter === s
                  ? 'bg-[#E8620A] text-white border-[#E8620A]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-[#E8620A] hover:text-[#E8620A]'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {[
                  { label: 'Quote #', field: 'quoteNumber' as SortField },
                  { label: 'Client', field: 'clientName' as SortField },
                  { label: 'Subject', field: null },
                  { label: 'Date', field: 'date' as SortField },
                  { label: 'Valid Until', field: null },
                  { label: 'Taxable Amt', field: null },
                  { label: 'GST', field: null },
                  { label: 'Grand Total', field: 'grandTotal' as SortField },
                  { label: 'Status', field: 'status' as SortField },
                  { label: 'Actions', field: null },
                ].map(col => (
                  <th
                    key={`th-${col.label}`}
                    onClick={() => col.field && toggleSort(col.field)}
                    className={`px-4 py-3 text-left text-xs font-600 text-slate-500 uppercase tracking-wider whitespace-nowrap ${col.field ? 'cursor-pointer hover:text-slate-800 select-none' : ''}`}
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      {col.field && <SortIcon field={col.field} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-16 text-center">
                    <FileText size={40} className="mx-auto text-slate-200 mb-3" />
                    <p className="text-slate-500 font-medium">No quotations found</p>
                    <p className="text-slate-400 text-xs mt-1">Try adjusting your search or filters</p>
                  </td>
                </tr>
              ) : paginated.map(q => (
                <tr key={q.id} className="hover:bg-slate-50/70 transition-colors group">
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1B2A4A]">{q.quoteNumber}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800 whitespace-nowrap">{q.clientName}</div>
                    <div className="text-xs text-slate-400 truncate max-w-[140px]">{q.clientCompany}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-[160px]">
                    <span className="truncate block">{q.subject}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap tabular-nums">{q.date}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap tabular-nums">{q.validUntil}</td>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap tabular-nums text-right">{formatINR(q.subtotal)}</td>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap tabular-nums text-right">{formatINR(q.totalGST)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap tabular-nums text-right">{formatINR(q.grandTotal)}</td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <button
                        onClick={() => setStatusMenuId(statusMenuId === q.id ? null : q.id)}
                        className="focus:outline-none"
                      >
                        <StatusBadge status={q.status} />
                      </button>
                      {statusMenuId === q.id && (
                        <div className="absolute z-20 top-8 left-0 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[130px]">
                          {STATUS_OPTIONS.map(s => (
                            <button
                              key={`sm-${q.id}-${s}`}
                              onClick={() => handleStatusChange(q, s)}
                              className={`w-full text-left px-3 py-2 text-xs font-medium hover:bg-slate-50 ${q.status === s ? 'text-[#E8620A]' : 'text-slate-700'}`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Link
                        href={`/quotation-preview?id=${q.id}`}
                        title="Preview"
                        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-[#1B2A4A] transition-colors"
                      >
                        <Eye size={15} />
                      </Link>
                      <Link
                        href={`/quotation-editor?id=${q.id}`}
                        title="Edit"
                        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-[#E8620A] transition-colors"
                      >
                        <Pencil size={15} />
                      </Link>
                      <button
                        onClick={() => handleDuplicate(q)}
                        title="Duplicate"
                        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-[#C9922A] transition-colors"
                      >
                        <Copy size={15} />
                      </button>
                      <button
                        onClick={() => setDeleteId(q.id)}
                        title="Delete"
                        className="p-1.5 rounded-md hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-slate-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#E8620A]/30 bg-white"
            >
              {PAGE_SIZES.map(s => <option key={`ps-${s}`} value={s}>{s}</option>)}
            </select>
            <span className="ml-2">{filtered.length} total</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md border border-slate-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5) {
                if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
              }
              return (
                <button
                  key={`page-${pageNum}`}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded-md text-xs font-medium transition-colors ${
                    page === pageNum
                      ? 'bg-[#E8620A] text-white'
                      : 'border border-slate-200 hover:bg-white text-slate-600'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-md border border-slate-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirm modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDeleteId(null)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Delete Quotation</h3>
                <p className="text-sm text-slate-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-all active:scale-95">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Close menus on outside click */}
      {(activeMenu || statusMenuId) && (
        <div className="fixed inset-0 z-10" onClick={() => { setActiveMenu(null); setStatusMenuId(null); }} />
      )}
    </div>
  );
}